import type { DirectorPendingItem } from '@/types/director'

/**
 * Fallakte v2 (P4.4): Hilfen für die Fallliste — Zeitraum, Tageszahl,
 * Frist-Gruppierung (K1: Gate-Fälle haben eine pausierte Frist).
 */

export type DeadlineGroupKey = 'overdue' | 'today' | 'this_week' | 'later' | 'paused' | 'none'

export interface DeadlineGroup {
  key: DeadlineGroupKey
  label: string
  items: DirectorPendingItem[]
}

const GROUP_ORDER: DeadlineGroupKey[] = ['overdue', 'today', 'this_week', 'later', 'none', 'paused']
const GROUP_LABELS: Record<DeadlineGroupKey, string> = {
  overdue: 'Überfällig',
  today: 'Frist heute',
  this_week: 'Diese Woche',
  later: 'Später',
  none: 'Ohne Frist',
  paused: 'Frist pausiert (Gate)',
}

/** De-CH-Datum kurz: «Mi 26.08.26». */
export function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('de-CH', {
      weekday: 'short', day: '2-digit', month: '2-digit', year: '2-digit',
    })
  } catch {
    return iso
  }
}

/** Zeitraum «Mi 26.08.26» bzw. «Di 25. – Do 27.08.26». */
export function formatDateRange(date: string | null | undefined, dateTo: string | null | undefined): string {
  if (!date) return '—'
  const from = formatShortDate(date)
  if (!dateTo || dateTo === date) return from
  return `${from} – ${formatShortDate(dateTo)}`
}

const INACTIVE_LESSON = new Set(['cancelled', 'director_removed'])

/** R16: distinkte aktive Lektionsdaten; Fallback Kalender-Spanne. */
export function lessonDaysCount(
  lessons: Array<{ date?: string | null; status?: string | null }> | null | undefined,
  date: string | null | undefined,
  dateTo: string | null | undefined,
): number {
  const dates = new Set(
    (lessons ?? [])
      .filter(l => !INACTIVE_LESSON.has(l.status ?? ''))
      .map(l => l.date)
      .filter((d): d is string => Boolean(d)),
  )
  if (dates.size > 0) return dates.size
  return daysCount(date, dateTo)
}

/** Tageszahl inkl. Enddatum; ohne date_to = 1 Tag. */
export function daysCount(date: string | null | undefined, dateTo: string | null | undefined): number {
  if (!date) return 0
  const start = new Date(date)
  const end = dateTo ? new Date(dateTo) : start
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0
  const msPerDay = 24 * 60 * 60 * 1000
  const days = Math.floor((stripTime(end).getTime() - stripTime(start).getTime()) / msPerDay) + 1
  return Math.max(1, days)
}

function stripTime(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function stripTimezone(local: Date): Date {
  return new Date(local.getFullYear(), local.getMonth(), local.getDate())
}

function sameLocalDay(a: Date, b: Date): boolean {
  return stripTimezone(a).getTime() === stripTimezone(b).getTime()
}

/**
 * K1: Frist-Gruppe eines Falls. Während director_pending/director_returned
 * ist die Antwortfrist eingefroren (gate_expires_at_backup) → 'paused',
 * statt einer irreführenden Countdown-Frist.
 */
export function deadlineGroup(
  expiresAt: string | null | undefined,
  status: string | null | undefined,
  now: Date = new Date(),
): DeadlineGroupKey {
  if (status === 'director_pending' || status === 'director_returned') {
    return 'paused'
  }
  if (!expiresAt) return 'none'
  const deadline = new Date(expiresAt)
  if (Number.isNaN(deadline.getTime())) return 'none'

  if (deadline.getTime() < now.getTime() && !sameLocalDay(deadline, now)) return 'overdue'
  if (sameLocalDay(deadline, now)) return deadline.getTime() < now.getTime() ? 'overdue' : 'today'

  const endOfWeek = new Date(now)
  endOfWeek.setDate(now.getDate() + (7 - (now.getDay() === 0 ? 7 : now.getDay())))
  endOfWeek.setHours(23, 59, 59, 999)
  if (deadline.getTime() <= endOfWeek.getTime()) return 'this_week'
  return 'later'
}

/** Gruppiert Pending-Items nach Frist; Überfälliges zuoberst (K1). */
export function groupPendingByDeadline(
  items: DirectorPendingItem[],
  now: Date = new Date(),
): DeadlineGroup[] {
  const groups = new Map<DeadlineGroupKey, DirectorPendingItem[]>()
  for (const item of items) {
    const key = deadlineGroup(item.expires_at, item.status, now)
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(item)
  }
  return GROUP_ORDER
    .filter(key => groups.has(key))
    .map(key => ({ key, label: GROUP_LABELS[key], items: groups.get(key)! }))
}

/** Frist-Anzeigetext: «heute, 16:00» / «26.08., 12:00» / null. */
export function formatDeadline(expiresAt: string | null | undefined): string | null {
  if (!expiresAt) return null
  try {
    const d = new Date(expiresAt)
    const time = d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' })
    const date = d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' })
    return `${date}, ${time}`
  } catch {
    return expiresAt
  }
}

/** Initiale für Avatar: «Anna Meier» → «AM». */
export function initialsOf(name: string | null | undefined): string {
  if (!name) return '??'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '??'
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase()
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
}

/** Deterministische Avatar-Farbe aus einem String-Hash. */
export function avatarColorOf(key: string | null | undefined): string {
  const palette = [
    '#e11d48', '#0ea5e9', '#8b5cf6', '#14b8a6', '#f59e0b',
    '#0891b2', '#64748b', '#d946ef', '#84cc16', '#f97316',
  ]
  let hash = 0
  const s = key ?? ''
  for (let i = 0; i < s.length; i++) {
    hash = (hash * 31 + s.charCodeAt(i)) >>> 0
  }
  return palette[hash % palette.length]!
}
